import { KeyValuePair, ThemeTokens } from '@acp/core-ui';
import { ActiveNoticePropertyTag, Notice, NoticeProperty } from '@acp/shared';
import {
    BorderProps, Card, CardBody, CardBodyProps, GridItem, GridItemProps,
    Hide,
    HStack,
    Image,
    Link,
    ResponsiveValue,
    Show,
    Text,
    Tooltip,
    VStack,
} from '@chakra-ui/react';
import { format, formatDistanceToNow } from 'date-fns';
import { Link as RouterLink } from 'react-router-dom';

const gap = 2;

const gridAreaProps: { [key: string]: GridItemProps } = {
    address: {
        gridArea: 'address',
        as: VStack,
        alignItems: 'start',
        justifyContent: 'flex-start',
        gap,
    },
    occupant: {
        gridArea: 'occupant',
    },
    reason: {
        gridArea: 'reason',
    },
    status: {
        gridArea: 'status',
    },
    actions: {
        gridArea: 'actions',
        as: HStack,
        alignSelf: 'flex-start',
        justifySelf: 'flex-end',
        gap,
    },
};

type AreaType = keyof typeof gridAreaProps;

interface RowColumnProps extends GridItemProps {
    area: AreaType;
}

export function RowLayout(
    {
        children,
        borderLeftColor = 'secondary.500',
    }:
    {
        children: React.ReactNode;
        borderLeftColor?: BorderProps['borderLeftColor'];
    }
) {
    return (
        <Card
            borderColor={ThemeTokens.Defaults.borderColor}
            borderWidth={1}
            borderLeftWidth={6}
            borderLeftColor={borderLeftColor}
            bg="white"
            variant="filled"
            w="full"
            shadow="sm"
            p={5}
        >
            {children}
        </Card>
    );
}

function RowBody({ children, as: Component, ...props }: CardBodyProps) {
    return (
        <CardBody as={Component} {...props}>
            {children}
        </CardBody>
    );
}

function RowColumn({ children, area, ...props }: RowColumnProps) {
    const gridItemAreaProps = gridAreaProps[area];

    return (
        <GridItem {...gridItemAreaProps} {...props}>
            {children}
        </GridItem>
    );
}

function RowIcon(
    { imgSrc, height }:
    { imgSrc: string; height?: ResponsiveValue<number | string> }
) {
    return (
        <Image src={imgSrc} h={height} />
    );
}

function Address({ property, tags }: { property: NoticeProperty; tags: string[] }) {
    return (
        <Link
            as={RouterLink}
            to={`/property/${property.compliancePropertyId}`}
            textDecoration="none"
            _hover={{ textDecoration: 'underline' }}
            fontWeight="medium"
            color={!tags.includes(ActiveNoticePropertyTag.Cancelled) ? 'black' : 'muted.500'}
        >
            <VStack alignItems="start" gap={0}>
                <Text>{property.addressLine1}</Text>
                <Text>{property.addressLine2}</Text>
            </VStack>
        </Link>
    );
}

function Reason(
    { activeNoticeDescription, notice }:
    { activeNoticeDescription: string; notice: Notice }
) {
    return (
        <>
            <Show above={ThemeTokens.Defaults.sidebarBreakpoint}>
                <Text>{activeNoticeDescription}</Text>
                <Tooltip label={`Notified on ${format(notice.createdAt, 'dd/MM/yyyy')}`} bgColor="white" color="black">
                    <Text fontSize="xs" fontWeight="bold" cursor="default" color="muted.500">
                        {`Notified ${formatDistanceToNow(notice.createdAt, { addSuffix: true })}`}
                    </Text>
                </Tooltip>
            </Show>
            <Hide above={ThemeTokens.Defaults.sidebarBreakpoint}>
                <KeyValuePair
                    label="Reason"
                    align="flex-start"
                    spacing={0}
                >
                    <Text>{activeNoticeDescription}</Text>
                    <Tooltip label={`Notified on ${format(notice.createdAt, 'dd/MM/yyyy')}`} bgColor="white" color="black">
                        <Text fontSize="xs" fontWeight="bold" cursor="default" color="muted.500">
                            {`Notified ${formatDistanceToNow(notice.createdAt, { addSuffix: true })}`}
                        </Text>
                    </Tooltip>
                </KeyValuePair>
            </Hide>
        </>
    );
}

RowLayout.Body = RowBody;
RowLayout.Column = RowColumn;
RowLayout.Icon = RowIcon;
RowLayout.Address = Address;
RowLayout.Reason = Reason;
